import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from 'react';
import {
  Dialog,
  usePreventTableScrollingRef,
  TableContent,
  TableHeaderCustomContent,
  TableContainer,
  SearchInput,
  INPUT_SIZES
} from 'cdk-radial';
import classNames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import './styles/viewFieldsDialog.scss';
import { API_TYPE } from './constants';

const API_ENVIRONMENTS = { PRODUCTION: 'production', TEST: 'test', DEV: 'dev' };
const DEFAULT_ENVIRONMENT = {
  label: 'Production',
  value: API_ENVIRONMENTS.PRODUCTION
};

const APIEnvironmentSelector = ({
  environments = [],
  onEnvironmentSelection,
  defaultEnvironment = DEFAULT_ENVIRONMENT
}) => {
  const [environment, setEnvironment] = useState(defaultEnvironment);
  const [isOpen, setIsOpen] = useState(false);
  const options = useMemo(() => {
    return environments.map(env =>
      typeof env === 'string' ? { label: env, value: env.toLowerCase() } : env
    );
  }, [environments]);

  const handleEnvironmentSelection = useCallback(
    env => {
      setEnvironment(env);
      onEnvironmentSelection(env?.value);
      setIsOpen(false);
    },
    [onEnvironmentSelection]
  );

  return (
    <div className="dropdown" data-testid="dropdown-menu" id="api-env-selector">
      <label className="dropdown-label">Select API Environment</label>
      <div className="selected-option" onClick={() => setIsOpen(!isOpen)}>
        {environment.label} <span className="arrow">&#9660;</span>
      </div>
      {isOpen && (
        <div className="custom-dropdown">
          {options.map(item => (
            <div
              key={item.value}
              className={`dropdown-item ${
                environment.value === item.value ? 'selected' : ''
              }`}
              onClick={() => handleEnvironmentSelection(item)}
            >
              {item.label}
              {environment.value === item.value && (
                <span className="check">&#10003;</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ApiFieldsCheckBoxCellRenderer = ({ data }) => (
  <div className="field-name-div">
    <span>{data?.name || 'N/A'}</span>
  </div>
);

const VersionCellRenderer = ({ data }) => {
  let { numberOfApiInstances, versions } = data || {};

  if (
    numberOfApiInstances > 1 &&
    numberOfApiInstances != versions.length &&
    versions[0] != 'nullVersion'
  ) {
    return (
      <div className="api_field_versions">{`Only in ${versions
        .map(v => ` v${v}`)
        .toString()}`}</div>
    );
  } else {
    return <></>;
  }
};

const ViewFieldsDialog = ({
  isViewFieldsOpen,
  setIsViewFieldsOpen,
  apiFields,
  apiData,
  apiFieldsTestEnv
}) => {
  const tableContentRef = usePreventTableScrollingRef();
  const [searchText, setSearchText] = useState('');
  const [isAPIEnvironmentProd, setIsAPIEnvironmentProd] = useState(true);

  const columns = useMemo(
    () => [
      {
        cellRendererFramework: ApiFieldsCheckBoxCellRenderer,
        field: 'name',
        headerName: 'Field Name',
        width: 450,
        resizable: true
      },
      {
        cellRendererFramework: VersionCellRenderer,
        field: 'versions',
        headerName: 'Version',
        width: 180,
        resizable: true,
        sortable: false,
        hide: apiData.kind === API_TYPE.ASYNC
      }
    ],
    [apiData]
  );

  const environmentOptions = useMemo(
    () =>
      apiData.kind === API_TYPE.REST
        ? [
            { label: 'Production', value: 'production' },
            { label: 'Test', value: 'test' }
          ]
        : [],
    [apiData.kind]
  );

  const handleGridReady = useCallback(
    gridParams => gridParams.api.sizeColumnsToFit(),
    []
  );

  const handleEnvironmentChange = useCallback(
    env =>
      setIsAPIEnvironmentProd(
        env?.toLowerCase() === API_ENVIRONMENTS.PRODUCTION
      ),
    []
  );

  const handleDialogClose = useCallback(() => {
    setSearchText('');
    setIsAPIEnvironmentProd(true);
    setIsViewFieldsOpen(false);
  }, [setIsViewFieldsOpen]);

  const filteredData = useMemo(() => {
    const fields = isAPIEnvironmentProd ? apiFields : apiFieldsTestEnv;
    if (!Array.isArray(fields)) return [];

    return fields
      .map(({ name, versions, numberOfApiInstances }) => ({
        name: name || 'N/A',
        versions: Array.isArray(versions) ? [...versions] : ['N/A'],
        numberOfApiInstances: numberOfApiInstances || 0
      }))
      .filter(({ name }) =>
        name.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [apiFields, apiFieldsTestEnv, isAPIEnvironmentProd, searchText]);

  return (
    <>
      {isViewFieldsOpen && <div className="view-fields-dialog-overlay" />}
      <Dialog
        data-testid="view-fields-dialog"
        className="view-fields-dialog"
        isOpen={isViewFieldsOpen}
        onClose={handleDialogClose}
        title={<div className="view-fields-dialog-title">API Fields</div>}
      >
        <h2 className="app-details-configure-fields">API: {apiData?.api}</h2>

        {apiData.kind === API_TYPE.REST && (
          <div className="api-environment-selector-container api-environment-selector-container__app_details">
            <APIEnvironmentSelector
              environments={environmentOptions}
              onEnvironmentSelection={handleEnvironmentChange}
              defaultEnvironment={DEFAULT_ENVIRONMENT}
            />
          </div>
        )}

        <TableContainer
          className={classNames(
            'apiFieldsTable',
            'ag-theme-balham',
            'view-fields-table-container'
          )}
          tableLayout="normal"
        >
          <TableHeaderCustomContent
            className="custom-header"
            customHeaderContent={
              <div className="view-fields-table-header">
                <SearchInput
                  clearButtonLabel="clearButtonLabel"
                  id="search-input-expanded"
                  isAlwaysExpanded
                  name="search-input-expanded"
                  onChange={setSearchText}
                  placeholder="Search Fields"
                  size={INPUT_SIZES.STANDARD}
                  value={searchText}
                />
              </div>
            }
          />
          <TableContent data-testid="view-fields-table" ref={tableContentRef}>
            <AgGridReact
              columnDefs={columns}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              onGridReady={handleGridReady}
              rowData={filteredData}
              overlayLoadingTemplate="Loading Fields..."
              overlayNoRowsTemplate="No fields to show"
              rowHeight={32}
              enableBrowserTooltips
            />
          </TableContent>
        </TableContainer>
      </Dialog>
    </>
  );
};

export default ViewFieldsDialog;
